import React, { Component } from 'react'
import shortid from "shortid"
import {Enum} from 'enumify'
import './SimpleSplitPane.css'

class PaneDirection extends Enum {}
PaneDirection.initEnum({
    HORIZONTAL: { className: "horizontal", 
    calculateOffset(startCoord, dragCoord) {
        return startCoord.x - dragCoord.x
    },
    getSize(ref) {
        return ref.current.clientWidth
    }},
    VERTICAL: { className: "vertical", 
    calculateOffset(startCoord, dragCoord) {
        return startCoord.y - dragCoord.y
    },
    getSize(ref) {
        return ref.current.clientHeight
    }},
})

class SimpleSplitPane extends Component {

    constructor(props) {
        super(props)
        this.paneRefs = []
        this.preferedSizes = []

        const cachedSizes = localStorage.getItem(this.props.cacheIdentifier);
        this.preferedSizes = cachedSizes ? JSON.parse(cachedSizes) : []
    }

    componentWillUnmount() {
        window.removeEventListener('touchmove', this.handleTouchMove)
        window.removeEventListener('touchend', this.handleTouchEnd)
        window.removeEventListener('touchcancel', this.handleTouchEnd)
        window.removeEventListener("mousemove", this.handleDrag)
        window.removeEventListener("mouseup", this.handleMouseUp)
    }

    handleDragStart = (startCoord, dividerIndex) => {
        this.dividerIndex = dividerIndex
        this.lastMouseCoord = startCoord
        this.wrapperRef.current.classList.add("resizing")
    }

    handleDragMove = (dragCoord) => {
        const offset = this.props.direction.calculateOffset(this.lastMouseCoord, dragCoord)
        const paneOneRef = this.paneRefs[this.dividerIndex]
        const paneTwoRef = this.paneRefs[this.dividerIndex + 1]
        const relativeSizePaneOne = Number(paneOneRef.current.style["flex-grow"])
        const relativeSizePaneTwo = Number(paneTwoRef.current.style["flex-grow"])
        const totalRelativeSizeOfPanes= relativeSizePaneOne + relativeSizePaneTwo
        const sizePaneOne = this.props.direction.getSize(paneOneRef)
        const sizePaneTwo = this.props.direction.getSize(paneTwoRef)
        const totalAbsoluteSizeOfPanes =  sizePaneOne + sizePaneTwo
        const newRelativeSizePaneOne = totalRelativeSizeOfPanes * ((sizePaneOne - offset) / totalAbsoluteSizeOfPanes)
        const newRelativeSizePaneTwo = totalRelativeSizeOfPanes - newRelativeSizePaneOne

        this.lastMouseCoord = dragCoord
        if(newRelativeSizePaneOne <= 0 || newRelativeSizePaneTwo <= 0) {
            return
        }
        paneOneRef.current.style["flex-grow"] = newRelativeSizePaneOne
        paneTwoRef.current.style["flex-grow"] = newRelativeSizePaneTwo

        this.props.onResize(this.dividerIndex, this.dividerIndex + 1)
    }

    handleDragStop = () => {
        this.storeSizes()
        this.wrapperRef.current.classList.remove("resizing") 
        this.props.onResizeEnd(this.dividerIndex, this.dividerIndex + 1)
    }

    handleDividerTouchStart = (event, dividerIndex) => {
        window.addEventListener('touchmove', this.handleTouchMove)
        window.addEventListener('touchend', this.handleTouchEnd)
        window.addEventListener('touchcancel', this.handleTouchEnd)
        event.preventDefault()
        
        this.handleDragStart({x: event.targetTouches[0].clientX, y: event.targetTouches[0].clientY}, dividerIndex)
    }

    handleDividerMouseDown = (event, dividerIndex) => {
        window.addEventListener("mousemove", this.handleMouseMove)
        window.addEventListener("mouseup", this.handleMouseUp)
        event.preventDefault()
        
        this.handleDragStart({x: event.clientX, y: event.clientY}, dividerIndex)
    }

    handleTouchEnd = (event) => {
        window.removeEventListener('touchmove', this.handleTouchMove)
        window.removeEventListener('touchend', this.handleTouchEnd)
        window.removeEventListener('touchcancel', this.handleTouchEnd)
        event.preventDefault()

        this.handleDragStop()
    }

    handleMouseUp = (event) => {
        window.removeEventListener("mousemove", this.handleMouseMove)
        window.removeEventListener("mouseup", this.handleMouseUp)
        event.preventDefault()

        this.handleDragStop()
    }
    
    handleTouchMove = (event) => {
        const {clientX, clientY} = event.touches[0]
        this.handleDragMove({x: clientX, y: clientY})
        event.preventDefault()
    }

    handleMouseMove = (event) => {
        this.handleDragMove({x: event.clientX, y: event.clientY})
        event.preventDefault()
    }

    storeSizes = () => {
        this.preferedSizes = this.paneRefs.map( ref => Number(ref.current.style["flex-grow"]) )
        localStorage.setItem(this.props.cacheIdentifier, JSON.stringify(this.preferedSizes));
    }

    shouldComponentUpdate(nextProps, nextState) {
        return nextProps.shouldControlUpdate
    }

    render() {
        const childCount = React.Children.count(this.props.children)
        // todo make sure this works 100% otherwise it will break everything
        this.preferedSizes = Array.from({length: childCount}, () => 100/childCount)

        this.paneRefs = Array.from({length: childCount}, () => React.createRef())

        const childs = this.props.children.reduce( (accumulatedChilds, child, index, original) => {
            const pane = <SimpleSplitPaneContent relativeSize={this.preferedSizes[index]} ref={this.paneRefs[index]} key={shortid.generate()}>{child}</SimpleSplitPaneContent>
            accumulatedChilds.push(pane)
            if(index < childCount - 1) {
                //const Tab = props => <div className="test2"></div>
                accumulatedChilds.push(<SimpleSplitPaneDivider dividerIndex={index} onMouseDown={this.handleDividerMouseDown} onTouchStart={this.handleDividerTouchStart} key={shortid.generate()}>{this.props.dividerContent}</SimpleSplitPaneDivider>)
            }
            return accumulatedChilds
        }, [])

        this.wrapperRef = React.createRef()
        return (
            <div ref={this.wrapperRef} className={'simple-split-pane ' + this.props.direction.className}>
               {childs}
            </div>
        )
    }
}

SimpleSplitPane.defaultProps = {
    shouldControlUpdate: false,
    direction: PaneDirection.VERTICAL,
    onResize: (indexOne, indexTwo) => {},
    onResizeEnd: (indexOne, indexTwo) => {},
}

export default SimpleSplitPane

const SimpleVerticalSplitPane = (props) => {
    return (
        <SimpleSplitPane direction={PaneDirection.VERTICAL} {...props}>
            {props.children}
        </SimpleSplitPane>
    )
}

const SimpleHorizontalSplitPane = (props) => {
    return (
        <SimpleSplitPane direction={PaneDirection.HORIZONTAL} {...props}>
            {props.children}
        </SimpleSplitPane>
        )
}

const SimpleSplitPaneContent = React.forwardRef((props, ref) => (
    <div ref={ref} style={{ flex: props.relativeSize }} className="simple-split-pane-content">{props.children}</div>
))

SimpleSplitPaneContent.defaultProps = {
    relativeSize: 1,
}

class SimpleSplitPaneDivider extends Component {

    handleMouseDown = (event) => {
        this.props.onMouseDown(event, this.props.dividerIndex)
    }

    handleTouchStart = (event) => {
        this.props.onTouchStart(event, this.props.dividerIndex)
    }

    render() {
        return (
            <div onTouchStart={this.handleTouchStart} onMouseDown={this.handleMouseDown} className="simple-split-pane-divider">{this.props.children}</div>
            )
    }
}

SimpleSplitPaneDivider.defaultProps = {
    onMouseDown: (event, dividerIndex) => {},
    onTouchStart: (event, dividerIndex) => {},
}

export { SimpleVerticalSplitPane, SimpleHorizontalSplitPane }


// WEBPACK FOOTER //
// ./src/SimpleSplitPane.js